import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatExpansionModule } from '@angular/material/expansion'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { MatSliderModule } from '@angular/material/slider'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { TranslateModule } from '@ngx-translate/core'
import { GbDashboardSharedUiModule } from 'libs/gb-dashboard/shared/ui/src/lib/gb-dashboard-shared-ui.module'
import { Subscription } from 'rxjs'
import {
  GbPresenterItemFilterDialogComponent,
  GbPresenterItemFilterDialogResult,
} from '../gb-presenter-item-filter-dialog/gb-presenter-item-filter-dialog.component'
import {
  defaultHonourRollWidgetHorizontal,
  defaultHonourRollWidgetVertical,
} from '../gb-presenter-edit/gb-presenter-default-widgets'
import { GbPresenterToolsTextComponent } from '../gb-presenter-tools-text/gb-presenter-tools-text.component'
import { HonorRollLayoutDirectionEnum } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { GbPresenterToolsAdvancedOptionsContainerComponent } from '../gb-presenter-tools-advanced-options-container/gb-presenter-tools-advanced-options-container.component'

@Component({
  selector: 'gb-presenter-tools-honour-roll',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatSliderModule,
    MatExpansionModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
    GbDashboardSharedUiModule,
    GbPresenterToolsTextComponent,
    GbPresenterToolsAdvancedOptionsContainerComponent,
  ],
  templateUrl: './gb-presenter-tools-honour-roll.component.html',
  styleUrl: './gb-presenter-tools-honour-roll.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterToolsHonourRollComponent implements OnInit, OnDestroy {
  @Input({ required: true }) toolsFormGroup!: FormGroup

  readonly scrollSpeedControl = new FormControl<number>(1, { nonNullable: true })

  private readonly subscription = new Subscription()

  HonorRollLayoutDirectionEnum = HonorRollLayoutDirectionEnum

  hideFields = ['font_size', 'font_style', 'color']

  borderStyleOptions = [
    { value: 'solid', label: 'PRESENTER.HONOUR_ROLL_TOOLS.BORDER_STYLES.SOLID' },
    { value: 'dashed', label: 'PRESENTER.HONOUR_ROLL_TOOLS.BORDER_STYLES.DASHED' },
    { value: 'dotted', label: 'PRESENTER.HONOUR_ROLL_TOOLS.BORDER_STYLES.DOTTED' },
    { value: 'double', label: 'PRESENTER.HONOUR_ROLL_TOOLS.BORDER_STYLES.DOUBLE' },
  ]

  participantsTransitionOptions = [
    { value: 'scroll-right', label: 'PRESENTER.HONOUR_ROLL_TOOLS.PARTICIPANTS_TRANSITION_OPTIONS.SCROLL_RIGHT' },
    { value: 'scroll-left', label: 'PRESENTER.HONOUR_ROLL_TOOLS.PARTICIPANTS_TRANSITION_OPTIONS.SCROLL_LEFT' },
    { value: 'scroll-top', label: 'PRESENTER.HONOUR_ROLL_TOOLS.PARTICIPANTS_TRANSITION_OPTIONS.SCROLL_UP' },
    { value: 'scroll-bottom', label: 'PRESENTER.HONOUR_ROLL_TOOLS.PARTICIPANTS_TRANSITION_OPTIONS.SCROLL_DOWN' },
  ]

  layoutDirectionOptions = [
    { value: 'vertical', label: 'PRESENTER.HONOUR_ROLL_TOOLS.LAYOUT_DIRECTIONS.VERTICAL' },
    { value: 'horizontal', label: 'PRESENTER.HONOUR_ROLL_TOOLS.LAYOUT_DIRECTIONS.HORIZONTAL' },
  ]

  get latestActivitiesControl(): FormControl {
    return this.toolsFormGroup.get('latest_activities') as FormControl
  }

  get transitionControl(): FormControl {
    return this.toolsFormGroup.get('transition') as FormControl
  }

  get rowsControl(): FormControl {
    return this.toolsFormGroup.get('rows') as FormControl
  }

  get columnsControl(): FormControl {
    return this.toolsFormGroup.get('columns') as FormControl
  }

  get layoutDirectionControl(): FormControl {
    return this.toolsFormGroup.get('layout_direction') as FormControl
  }

  get slideIntervalControl(): FormControl {
    return this.toolsFormGroup.get('slide_interval') as FormControl
  }

  get tableBackgroundColorControl(): FormControl {
    return this.toolsFormGroup.get('table_background_color') as FormControl
  }

  get tablePrimaryColorControl(): FormControl {
    return this.toolsFormGroup.get('table_primary_color') as FormControl
  }

  get tableAccentColorControl(): FormControl {
    return this.toolsFormGroup.get('table_accent_color') as FormControl
  }

  get opacityControl(): FormControl {
    return this.toolsFormGroup.get('opacity') as FormControl
  }

  get fontSizeControl(): FormControl {
    return this.toolsFormGroup.get('font_size') as FormControl
  }

  get showItemAliasControl(): FormControl {
    return this.toolsFormGroup.get('show_item_alias') as FormControl
  }

  get showItemNameControl(): FormControl {
    return this.toolsFormGroup.get('show_item_name') as FormControl
  }

  get borderWidthControl(): FormControl {
    return this.toolsFormGroup.get('border_width') as FormControl
  }

  get borderColorControl(): FormControl {
    return this.toolsFormGroup.get('border_color') as FormControl
  }

  get borderStyleControl(): FormControl {
    return this.toolsFormGroup.get('border_style') as FormControl
  }

  get hiddenItemsControl(): FormControl {
    return this.toolsFormGroup.get('hidden_items') as FormControl
  }

  constructor(private gbDashboardDialogService: GbDashboardDialogService) {}

  ngOnInit(): void {
    this.syncScrollSpeedControlFromSlideInterval()

    this.subscription.add(
      this.slideIntervalControl.valueChanges.subscribe(() => {
        this.syncScrollSpeedControlFromSlideInterval()
      }),
    )

    this.subscription.add(
      this.scrollSpeedControl.valueChanges.subscribe((speed) => {
        const durationSeconds = this.mapScrollSpeedToDurationSeconds(speed)
        this.slideIntervalControl.setValue(durationSeconds, { emitEvent: false })
        this.slideIntervalControl.markAsDirty()
        this.toolsFormGroup.markAsDirty()
      }),
    )
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  openFilterItemsDialog(): void {
    const hiddenItemUuids = (this.hiddenItemsControl?.value || []) as string[]

    const dialogRef = this.gbDashboardDialogService.openDialog(GbPresenterItemFilterDialogComponent, {
      width: '750px',
      maxWidth: '95vw',
      data: { hiddenItemUuids },
    })

    dialogRef.afterClosed().subscribe((result: GbPresenterItemFilterDialogResult | undefined) => {
      if (!result) return

      this.hiddenItemsControl.setValue(result.hiddenItemUuids)
      this.hiddenItemsControl.markAsDirty()
      this.toolsFormGroup.markAsDirty()
    })
  }

  setDefaults(orientation: HonorRollLayoutDirectionEnum) {
    if (orientation === HonorRollLayoutDirectionEnum.HORIZONTAL) {
      this.toolsFormGroup.patchValue(defaultHonourRollWidgetHorizontal)
      this.toolsFormGroup.markAsDirty()
    }
    if (orientation === HonorRollLayoutDirectionEnum.VERTICAL) {
      this.toolsFormGroup.patchValue(defaultHonourRollWidgetVertical)
      this.toolsFormGroup.markAsDirty()
    }
  }

  private syncScrollSpeedControlFromSlideInterval(): void {
    const durationSeconds = Math.max(1, Math.min(60, Number(this.slideIntervalControl.value ?? 10)))
    const speed = this.mapDurationSecondsToScrollSpeed(durationSeconds)
    this.scrollSpeedControl.setValue(speed, { emitEvent: false })
  }

  private mapDurationSecondsToScrollSpeed(durationSeconds: number): number {
    return 61 - durationSeconds
  }

  private mapScrollSpeedToDurationSeconds(speed: number): number {
    const clampedSpeed = Math.max(1, Math.min(60, Number(speed)))
    return 61 - clampedSpeed
  }
}
